import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { DEFAULT_BRAND_NAME, DEFAULT_SITE_URL, getSeoForPath, normalizeSiteUrl } from '../lib/seo';

const STRUCTURED_DATA_ID = 'structured-data';

function upsertMeta(attribute, value, content) {
  let element = document.head.querySelector(`meta[${attribute}="${value}"]`);
  if (!content) {
    element?.remove();
    return;
  }

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, value);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
}

function upsertCanonical(href) {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
}

function syncStructuredData(structuredData) {
  const existing = document.getElementById(STRUCTURED_DATA_ID);
  if (!structuredData) {
    existing?.remove();
    return;
  }

  if (existing) {
    existing.textContent = structuredData;
    return;
  }

  const script = document.createElement('script');
  script.id = STRUCTURED_DATA_ID;
  script.type = 'application/ld+json';
  script.textContent = structuredData;
  document.head.appendChild(script);
}

export default function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const siteUrl = normalizeSiteUrl(import.meta.env.VITE_SITE_URL || window.location.origin || DEFAULT_SITE_URL);
    const seo = getSeoForPath(location.pathname, siteUrl);
    const title = seo.title ?? DEFAULT_BRAND_NAME;

    document.title = title;
    upsertMeta('name', 'description', seo.description);
    upsertMeta('name', 'robots', seo.robots);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', seo.description);
    upsertMeta('property', 'og:url', seo.canonicalUrl);
    upsertMeta('property', 'og:site_name', DEFAULT_BRAND_NAME);
    upsertMeta('property', 'og:image', seo.ogImageUrl);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', seo.description);
    upsertMeta('name', 'twitter:image', seo.ogImageUrl);
    upsertCanonical(seo.canonicalUrl);
    syncStructuredData(seo.structuredData);
  }, [location.pathname]);

  return null;
}
